/**
 * HTTP请求封装 - 使用axios
 * 统一处理请求和响应
 */
import axios from 'axios'

// 创建axios实例
const service = axios.create({
    baseURL: '/', // 开发环境使用代理，生产环境使用完整URL
    timeout: 15000 // 请求超时时间（15秒）
})

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 从localStorage获取token
        const token = localStorage.getItem('token')

        // 如果token存在，添加到请求头
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        // 如果是FormData，不设置Content-Type，让axios自动设置
        // 如果是普通对象，设置Content-Type为application/json
        if (config.data && !(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json'
        }

        return config
    },
    error => {
        // 请求错误处理
        console.error('请求错误:', error)
        return Promise.reject(error)
    }
)

// 响应拦截器
service.interceptors.response.use(
    response => {
        // 获取响应数据
        const res = response.data

        // 检查业务状态码
        if (res.code === 200) {
            // 请求成功，返回数据
            return res
        } else {
            // 业务错误
            console.error('业务错误:', res.message)
            return Promise.reject(new Error(res.message || '请求失败'))
        }
    },
    error => {
        // HTTP错误处理
        console.error('HTTP错误:', error)

        let message = '请求失败'

        if (error.response) {
            // 服务器返回了错误状态码
            switch (error.response.status) {
                case 400:
                    message = '请求参数错误'
                    break
                case 401:
                    message = '未授权，请重新登录'
                    // 清除token
                    localStorage.removeItem('token')
                    localStorage.removeItem('currentUser')
                    // 可以在这里跳转到登录页
                    // window.location.href = '/login'
                    break
                case 403:
                    message = '拒绝访问'
                    break
                case 404:
                    message = '请求的资源不存在'
                    break
                case 500:
                    message = '服务器内部错误'
                    break
                default:
                    message = `连接错误 ${error.response.status}`
            }
        } else if (error.request) {
            // 请求已发出，但没有收到响应
            message = '网络连接失败，请检查网络'
        } else {
            // 其他错误
            message = error.message || '请求失败'
        }

        return Promise.reject(new Error(message))
    }
)

/**
 * GET请求
 * @param {string} url - 请求URL
 * @param {Object} params - 查询参数
 * @param {Object} config - axios配置
 * @returns {Promise} 响应数据
 */
export function get(url, params = {}, config = {}) {
    return service({
        method: 'get',
        url,
        params,
        ...config
    })
}

/**
 * POST请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求体数据
 * @param {Object} config - axios配置
 * @returns {Promise} 响应数据
 */
export function post(url, data = {}, config = {}) {
    return service({
        method: 'post',
        url,
        data,
        ...config
    })
}

/**
 * PUT请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求体数据
 * @param {Object} config - axios配置
 * @returns {Promise} 响应数据
 */
export function put(url, data = {}, config = {}) {
    return service({
        method: 'put',
        url,
        data,
        ...config
    })
}

/**
 * DELETE请求
 * @param {string} url - 请求URL
 * @param {Object} config - axios配置
 * @returns {Promise} 响应数据
 */
export function del(url, config = {}) {
    return service({
        method: 'delete',
        url,
        ...config
    })
}

// 导出axios实例，供特殊需求使用
export default service
